// The nav's workspace switches as hooks (the rules are in ./navFlags): the
// live settings answer once it is here, the switches this browser
// remembered for the workspace until then, and the answer written back
// whenever it changes.
//
// For the chrome only (the sidebar, the top bar's hub tabs, the phone's
// More rows, the Settings sections and the palette) and for which counts to
// start reading at once. A page that guards itself (Todos, Tasks,
// Workflows, Subagents, Schedules) keeps reading the live answer alone.

import { useEffect, useMemo, useState } from "react";

import { useCustomerSettings } from "@/api/customerSettings";
import { useWorkflowCapacity } from "@/api/workerSlots";

import { chooseNavFlags, pickNavFlags, readRemembered, rememberDeployments, rememberNavFlags, type KnownFlags, type RememberedNav } from "./navFlags";
import { withDeploymentsFlag, type NavFlags } from "./navModel";

/** What this workspace left behind in this browser, read once per workspace. */
export function useRememberedNav(customerId: string | null | undefined): RememberedNav | undefined {
  const c = customerId || null;
  return useMemo(() => readRemembered(c), [c]);
}

/** Writes a settings answer's switches back whenever they change (a no-op
 *  when they are what is kept already). */
export function useRememberLiveFlags(customerId: string | null | undefined, live: KnownFlags | undefined): void {
  const c = customerId || null;
  useEffect(() => {
    if (c && live) rememberNavFlags(c, live);
  }, [c, live]);
}

/** The switches the nav draws with: live once answered, else remembered,
 *  else undefined (nothing known yet: a first visit in this browser); plus
 *  `deployments` once the workflows capacity route says the workspace has
 *  Deployments, remembered the same way so the tab does not pop in (unknown
 *  or off: the Deployments page stays hidden). */
export function useNavFlags(customerId: string | null | undefined): NavFlags {
  const c = customerId || null;
  const settings = useCustomerSettings(c).data;
  const live = useMemo(() => pickNavFlags(settings), [settings]);
  const kept = useRememberedNav(c);
  const remembered = kept?.flags;
  useRememberLiveFlags(c, live);
  const liveDeployments = useWorkflowCapacity(c).data?.deployments;
  useEffect(() => {
    if (c && typeof liveDeployments === "boolean") rememberDeployments(c, liveDeployments);
  }, [c, liveDeployments]);
  const deployments = liveDeployments ?? kept?.deployments;
  return useMemo(() => withDeploymentsFlag(chooseNavFlags(live, remembered), deployments), [live, remembered, deployments]);
}

/** How long a first visit keeps the gated part of the nav out of sight
 *  while the switches load, so it draws once instead of growing rows. */
export const FIRST_DRAW_WAIT_MS = 1200;

/** True once `known`, or once `wait` ms have passed since the first render. */
export function useFirstDraw(known: boolean, wait = FIRST_DRAW_WAIT_MS): boolean {
  const [waited, setWaited] = useState(false);
  useEffect(() => {
    if (known || waited) return;
    const timer = window.setTimeout(() => setWaited(true), wait);
    return () => window.clearTimeout(timer);
  }, [known, waited, wait]);
  return known || waited;
}
